export interface LoginRequest {
  identifier: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
  userId: string;
  tenantId: string;
  organizationId?: string;
  email: string;
  fullName: string;
  companyName: string;
  roleName: string;
  roles: string[];
  permissions: string[];
  enabledFeatures?: string[];
  employeeId?: string;
  Employee_Id?: string;
}

export interface SendForgotPasswordOtpRequest {
  email: string;
  mobileNumber: string;
}

export interface SendForgotPasswordOtpResponse {
  otpId: string;
  expiresAt: string;
  mobileNumber: string;
  message: string;
}

export interface VerifyForgotPasswordOtpRequest {
  otpId: string;
  email: string;
  mobileNumber: string;
  otp: string;
}

export interface VerifyForgotPasswordOtpResponse {
  verificationToken: string;
  message: string;
}

export interface ResetForgotPasswordRequest {
  otpId: string;
  email: string;
  mobileNumber: string;
  verificationToken: string;
  newPassword: string;
  confirmPassword: string;
}

// Dropdown item from master APIs
export interface DropdownItem {
  id: string | number;
  name: string;
}

export interface Employee {
  id: string;
  employeeCode: string;
  fullName: string;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  mobileNumber: string | null;
  email: string | null;
  gender: string | null; // Guid as string
  bloodGroup: string | null; // Guid as string
  maritalStatus: string | null; // Guid as string
  nationality: string | null;
  religion: string | null;
  category: string | null;
  aadhaarNumber: string | null;
  panNumber: string | null;
  passportNumber: string | null;
  drivingLicense: string | null;
  uanNumber: string | null;
  bankAccountHolderName: string | null;
  bankName: string | null;
  bankBranchName: string | null;
  bankAccountNumber: string | null;
  bankIfscCode: string | null;
  dateOfBirth: string | null;
  confirmationDate: string | null;
  employmentType: string | null; // Guid as string
  departmentId: string | null;
  departmentName: string | null;
  designationId: string | null;
  designationName: string | null;
  branchId: string | null;
  branchName: string | null;
  shiftId: string | null;
  salaryStructureId: string | null;
  reportingManagerId: string | null;
  reportingManagerName: string | null;
  workLocation: string | null;
  probationPeriod: number | null;
  supervisorId: string | null;
  supervisorName: string | null;
  hrManagerId: string | null;
  hrManagerName: string | null;
  mentorId: string | null;
  mentorName: string | null;
  reviewerId: string | null;
  reviewerName: string | null;
  alternateMobile: string | null;
  personalEmail: string | null;
  officialEmail: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  pincode: string | null;
  organizationId: string | null;
  joinedDate: string | null;
  employmentStatus: number | null; // Guid as string
  employmentStatusName: string;
  allowMobileCheckin: boolean;
  allowMultipleCheckin: boolean;
  allowFaceAttendance: boolean;
  restrictLocationCheckin: boolean;
  allowOvertime: boolean;
  allowLateMark: boolean;
  attendanceTracking: string | null;
  payrollEnabled: boolean;
  subscribePayrollEmails: boolean;
  subscribeLeaveEmails: boolean;
  subscribeAttendanceEmails: boolean;
  subscribeAnnouncements: boolean;
  biometricUserId: string | null;
  profileImagePath: string | null;
  /** Browser-ready image URL from the API (blob URLs include a short-lived read SAS token). */
  profileImageUrl: string | null;
  isActive: boolean;
}

export interface EmployeeRoleDto {
  id: string;
  roleId: string;
  roleName: string;
}

export interface EmergencyContactDto {
  id: string;
  contactName: string;
  relationship: string | null;
  mobileNumber: string | null;
  alternateNumber: string | null;
}

export interface DependentDto {
  id: string;
  dependentName: string;
  relationship: string | null;
  dateOfBirth: string | null;
  occupation: string | null;
}

export interface SubscriberGroupDto {
  id: string;
  groupName: string;
  subscriberEmail: string | null;
  isActive: boolean;
}

export interface EmployeeDocumentDto {
  id: string;
  documentType: string;
  documentName: string;
  documentNumber: string | null;
  issueDate: string | null;
  expiryDate: string | null;
  remarks: string | null;
  filePath: string;
  fileSize: string;
  uploadedAt: string;
}

export interface EmployeeProfile extends Employee {
  genderId?: string | null;
  genderName?: string | null;
  bloodGroupId?: string | null;
  bloodGroupName?: string | null;
  maritalStatusId?: string | null;
  maritalStatusName?: string | null;
  nationalityId?: string | null;
  nationalityName?: string | null;
  religionId?: string | null;
  religionName?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  shiftName?: string | null;
  cityId?: string | null;
  cityName?: string | null;
  stateId?: string | null;
  stateName?: string | null;
  countryId?: string | null;
  countryName?: string | null;
  organizationName?: string | null;
  hasLoginCredentials: boolean;
  roles: EmployeeRoleDto[];
  emergencyContacts: EmergencyContactDto[];
  dependents: DependentDto[];
  subscriberGroups: SubscriberGroupDto[];
  employeeDocuments: EmployeeDocumentDto[];
}

export interface EmployeeDirectoryItem {
  id: string;
  employeeCode: string;
  fullName: string;
  mobileNumber: string | null;
  email: string | null;
  officialEmail?: string | null;
  personalEmail?: string | null;
  profileImagePath: string | null;
  /** Browser-ready image URL from the API (blob URLs include a short-lived read SAS token). */
  profileImageUrl?: string | null;
  organizationId?: string | null;
  organizationName?: string | null;
  branchId?: string | null;
  branchName?: string | null;
  departmentId?: string | null;
  departmentName?: string | null;
  designationId?: string | null;
  designationName?: string | null;
  workLocation?: string | null;
}

export interface CreateEmployeeQuick {
  fullName: string;
  mobileNumber?: string;
  employeeCode: string;
  organizationId?: string;
  gender?: string; // Guid as string
  allowMobileCheckin: boolean;
  allowMultipleCheckin: boolean;
  allowFaceAttendance: boolean;
  restrictLocationCheckin: boolean;
}

export interface BulkUploadEmployeeRow {
  fullName: string;
  contactNumber: string;
  email: string;
  employeeCode: string;
  gender: string;
}

export interface BulkUploadEmployeeError {
  rowNumber: number;
  employeeCode?: string | null;
  message: string;
}

export interface BulkUploadedEmployeeInfo {
  employeeId: string;
  fullName: string;
  contactNumber: string;
  email: string;
  employeeCode: string;
  gender: string;
}

export interface BulkUploadEmployeesResponse {
  totalRows: number;
  successCount: number;
  failedCount: number;
  successfulEmployees: BulkUploadedEmployeeInfo[];
  errors: BulkUploadEmployeeError[];
}

export interface NotificationItem {
  id: string;
  employeeId?: string | null;
  managerId?: string | null;
  title: string;
  message: string;
  type: string;
  isRead: boolean;
  isActionable: boolean;
  referenceId?: string | null;
  createdAt: string;
  readAt?: string | null;
}

export interface NotificationListResponse {
  totalCount: number;
  unreadCount: number;
  items: NotificationItem[];
}

export interface NotificationPreference {
  emailEnabled: boolean;
  pushEnabled: boolean;
  smsEnabled: boolean;
}

export interface CreateNotificationRequest {
  userId?: string;
  title: string;
  message: string;
  type: string;
  isActionable: boolean;
  referenceId?: string;
}

export interface UpdateEmployeeBasicDetails {
  id: string;
  employeeCode: string;
  fullName: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  gender?: string; // Guid as string
  bloodGroup?: string; // Guid as string
  dateOfBirth?: string;
  maritalStatus?: string;
  nationality?: string;
  religion?: string;
  category?: string;
  aadhaarNumber?: string;
  panNumber?: string;
  passportNumber?: string;
  drivingLicense?: string;
  uanNumber?: string;
  confirmationDate?: string;
  employmentType?: string; // Guid as string
  joinedDate?: string;
  organizationId?: string;
  employmentStatus: number; // Guid as string
}

export interface UpdateEmployeeJobDetails {
  id: string;
  departmentId?: string;
  designationId?: string;
  branchId?: string;
  shiftId?: string;
  salaryStructureId?: string;
  workLocation?: string;
  probationPeriod?: number;
  joinedDate?: string;
  employmentStatus?: number;
}

export interface UpdateEmployeeBankDetails {
  id: string;
  bankAccountHolderName?: string;
  bankName?: string;
  bankBranchName?: string;
  bankAccountNumber?: string;
  bankIfscCode?: string;
}

export interface UpdateEmployeeContactDetails {
  id: string;
  mobileNumber?: string;
  alternateMobile?: string;
  personalEmail?: string;
  officialEmail?: string;
  email?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
}

export interface UpdateEmployeeSupervisor {
  id: string;
  supervisorId?: string;
  hrManagerId?: string;
  mentorId?: string;
  reviewerId?: string;
}

export interface UpdateEmployeeSettings {
  id: string;
  allowMobileCheckin: boolean;
  allowMultipleCheckin: boolean;
  restrictLocationCheckin: boolean;
  allowOvertime: boolean;
  allowLateMark: boolean;
  attendanceTracking?: string;
  payrollEnabled: boolean;
  isActive: boolean;
}

export interface UpdateEmployeeRoles {
  id: string;
  roleIds: string[];
}

export interface UpdateEmployeeEmailSubscriptions {
  id: string;
  subscribePayrollEmails: boolean;
  subscribeLeaveEmails: boolean;
  subscribeAttendanceEmails: boolean;
  subscribeAnnouncements: boolean;
}

export interface AddEmergencyContact {
  employeeId: string;
  contactName: string;
  relationship?: string;
  mobileNumber?: string;
  alternateNumber?: string;
}

export interface UpdateEmergencyContact {
  id: string;
  contactName: string;
  relationship?: string;
  mobileNumber?: string;
  alternateNumber?: string;
}

export interface AddDependent {
  employeeId: string;
  dependentName: string;
  relationship?: string;
  dateOfBirth?: string;
  occupation?: string;
}

export interface UpdateDependent {
  id: string;
  dependentName: string;
  relationship?: string;
  dateOfBirth?: string;
  occupation?: string;
}

export interface AddSubscriberGroup {
  employeeId: string;
  groupName: string;
  subscriberEmail?: string;
  isActive: boolean;
}

export interface UpdateSubscriberGroup {
  id: string;
  groupName: string;
  subscriberEmail?: string;
  isActive: boolean;
}

export interface UploadEmployeeDocument {
  employeeId: string;
  documentName: string;
  documentType: string;
  documentNumber?: string;
  issueDate?: string;
  expiryDate?: string;
  remarks?: string;
  filePath: string;
  fileSize: string;
}

export interface AttendanceRecord {
  id: string;
  organization: string;
  employeeCode: string;
  employeeName: string;
  date: string;
  time: string;
  type: string;
  modeOfEntry?: string;
  locationName?: string;
  viewLocation?: string;
  entityName?: string;
  entityType?: string;
  reasonProvided?: string;
  recordedBy?: string;
  remarks?: string;
  department?: string;
  designation?: string;
  branch?: string;
  selfieImagePath?: string;
  attendanceStatus?: string;
  isLate?: boolean;
  isEarlyCheckOut?: boolean;
  isHoliday?: boolean;
  isRegularized?: boolean;
  checkInTime?: string;
  checkOutTime?: string;
}

// Regularization Request
export interface RegularizationRequest {
  id: string;
  employeeId: string;
  employeeName: string;
  date: string;
  requestedCheckIn: string;
  requestedCheckOut: string;
  reason: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  approvedBy?: string;
  approvedAt?: string;
  rejectionReason?: string;
}

// Shift Management
export interface Shift {
  id: string;
  shiftName: string;
  startTime: string;
  endTime: string;
  breakDuration: number; // in minutes
  graceTime: number; // in minutes
  isActive: boolean;
}

export interface WorkWeek {
  id: string;
  workWeekName: string;
  workingDays: string[]; // e.g., ['Monday', 'Tuesday', ...]
  weeklyOff: string[]; // e.g., ['Saturday', 'Sunday']
}

export interface EmployeeShiftMapping {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  department: string;
  shiftId: string;
  shiftName: string;
  startTime: string;
  endTime: string;
  effectiveDate: string;
}

// Leave Management
export interface LeavePeriod {
  id: string;
  leavePeriodName: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

export interface LeaveType {
  id: string;
  leaveName: string;
  maxDays: number;
  carryForward: boolean;
  carryForwardLimit?: number;
  requiresApproval: boolean;
  isActive: boolean;
}

export interface LeaveEntitlement {
  id: string;
  leaveTypeId: string;
  leaveTypeName: string;
  entitledDays: number;
  usedDays: number;
  balanceDays: number;
}

// Manual OT/HOT
export interface ManualOTEntry {
  id: string;
  employeeId: string;
  employeeName: string;
  date: string;
  otHours: number;
  hotHours: number;
  remarks?: string;
  createdAt: string;
  createdBy: string;
}

// Attendance Rules
export interface AttendanceRule {
  id: string;
  ruleName: string;
  ruleType: 'MinWorkingHours' | 'LateArrival' | 'EarlyCheckout' | 'Other';
  value: string;
  description?: string;
  isActive: boolean;
}

export interface User {
  userId: string;
  tenantId: string;
  organizationId?: string;
  email: string;
  fullName: string;
  companyName: string;
  roleName: string;
  roles: string[];
  permissions: string[];
  enabledFeatures?: string[];
  token: string;
  refreshToken?: string;
  refreshTokenExpiresAt?: string;
  employeeId?: string;
}

// Location Management
export interface LocationDto {
  id: string;
  locationName: string;
  address: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  latitude: number;
  longitude: number;
  radiusInMeters: number;
  locationCategoryId: string | null;
  locationCategoryName: string | null;
  isActive: boolean;
  description: string | null;
  whitelistedEmployeeCount: number;
  createdAt: string;
}

export interface LocationListResponse {
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: LocationDto[];
}

export interface LocationCategoryDto {
  id: string;
  categoryName: string;
  description: string | null;
  isActive: boolean;
  locationCount: number;
  createdAt: string;
}

export interface LocationWhitelistDto {
  id: string;
  locationId: string;
  locationName: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string | null;
  isActive: boolean;
  remarks: string | null;
  createdAt: string;
}

export interface CreateLocation {
  locationName: string;
  address: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  latitude: number;
  longitude: number;
  radiusInMeters: number;
  locationCategoryId?: string;
  description?: string;
}

export interface UpdateLocation extends CreateLocation {
  id: string;
  isActive: boolean;
}

export interface CreateLocationCategory {
  categoryName: string;
  description?: string;
}

export interface UpdateLocationCategory extends CreateLocationCategory {
  id: string;
  isActive: boolean;
}

export interface AddLocationWhitelist {
  locationId: string;
  employeeId: string;
  remarks?: string;
}

export interface UpdateLocationWhitelist extends AddLocationWhitelist {
  isActive: boolean;
}

export const EmploymentStatusLabels: Record<number, string> = {
  0: 'Working',
  1: 'Resigned',
  2: 'Terminated',
  3: 'Absoconding',
  4: 'Retired',
  5: 'On Probation',
  6: 'Internship',
  7: 'Contract',
  8: 'Freelance',
  9: 'Passed Away',
};

export const EmploymentStatusColors: Record<
  number,
  'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
> = {
  0: 'success',    // Working
  1: 'default',    // Resigned
  2: 'error',      // Terminated
  3: 'error',      // Absconding
  4: 'info',       // Retired
  5: 'warning',    // On Probation
  6: 'secondary',  // Internship
  7: 'primary',    // Contract
  8: 'primary',    // Freelance
  9: 'default',    // Passed Away
};

// Registration Requests
export interface RegistrationRequest {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string | null;
  createdAt: string;
  status: string;
}

export interface RegistrationRequestDetail extends RegistrationRequest {
  gender: string | null; // Guid as string
  dateOfBirth: string | null;
  maritalStatus: string | null;
  aadhaarNumber: string | null;
  panNumber: string | null;
  departmentId: string | null;
  departmentName: string | null;
  designationId: string | null;
  designationName: string | null;
  branchId: string | null;
  branchName: string | null;
  employmentType: string | null;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  rejectionReason: string | null;
}

export interface SubmitRegistration {
  tenantId: string;
  fullName: string;
  verificationToken: string;
  firstName?: string;
  lastName?: string;
  gender?: string; // Guid as string
  dateOfBirth?: string;
  maritalStatus?: string;
  email?: string;
  mobileNumber: string;
  aadhaarNumber?: string;
  panNumber?: string;
  departmentId?: string;
  designationId?: string;
  branchId?: string;
  employmentType?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  country?: string;
}

export interface PublicRegistrationContext {
  tenantId: string;
  organizationName: string;
  genders: DropdownItem[];
  maritalStatuses: DropdownItem[];
}

export interface SendRegistrationOtpRequest {
  tenantId: string;
  mobileNumber: string;
}

export interface SendRegistrationOtpResult {
  otpId: string;
  expiresAt: string;
  mobileNumber: string;
}

export interface VerifyRegistrationOtpRequest {
  tenantId: string;
  otpId: string;
  mobileNumber: string;
  otp: string;
}

export interface VerifyRegistrationOtpResult {
  verificationToken: string;
}

// ═══════════ Leave Management Types ═══════════

export interface LeaveTypeDto {
  id: string;
  name: string;
  description: string | null;
  maxDaysPerYear: number;
  isCarryForward: boolean;
  maxCarryForwardDays: number;
  isPaidLeave: boolean;
  isActive: boolean;
  allowHalfDay: boolean;
  includeHolidays: boolean;
  includeWeekends: boolean;
  sortOrder: number;
  colorCode: string | null;
}

export interface CreateLeaveType {
  name: string;
  description?: string;
  maxDaysPerYear: number;
  isCarryForward: boolean;
  maxCarryForwardDays: number;
  isPaidLeave: boolean;
  allowHalfDay: boolean;
  includeHolidays: boolean;
  includeWeekends: boolean;
  sortOrder: number;
  colorCode?: string;
}

export interface UpdateLeaveType extends CreateLeaveType {
  id: string;
  isActive: boolean;
}

export interface LeaveTypeConfigurationDto {
  id: string;
  leaveTypeId: string;
  entitlementDays: number;
  carryForward: boolean;
  maxCarryForwardDays: number;
  allowHalfDay: boolean;
  allowNegativeBalance: boolean;
  attachmentRequired: boolean;
  approvalRequired: boolean;
  isActive: boolean;
}

export interface SaveLeaveTypeConfiguration {
  leaveTypeId: string;
  entitlementDays: number;
  carryForward: boolean;
  maxCarryForwardDays: number;
  allowHalfDay: boolean;
  allowNegativeBalance: boolean;
  attachmentRequired: boolean;
  approvalRequired: boolean;
  isActive: boolean;
}

export interface LeavePeriodDto {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

export interface CreateLeavePeriod {
  name: string;
  startDate: string;
  endDate: string;
}

export interface UpdateLeavePeriod extends CreateLeavePeriod {
  id: string;
  isActive: boolean;
}

export interface LeaveEntitlementDto {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  leaveTypeId: string;
  leaveTypeName: string;
  leavePeriodId: string;
  leavePeriodName: string;
  totalDays: number;
  usedDays: number;
  remainingDays: number;
  carryForwardDays: number;
  validFrom: string;
  validTo: string;
  isActive: boolean;
}

export interface LeaveEntitlementListResponse {
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  items: LeaveEntitlementDto[];
}

export interface CreateLeaveEntitlement {
  employeeId: string;
  leaveTypeId: string;
  leavePeriodId: string;
  totalDays: number;
  carryForwardDays: number;
}

export interface CreateLeaveEntitlementsResponse {
  count: number;
  skippedCount: number;
  createdIds: string[];
}

export interface BulkCreateLeaveEntitlement {
  employeeIds: string[];
  leaveTypeId: string;
  leavePeriodId: string;
  totalDays: number;
  carryForwardDays: number;
}

export interface UpdateLeaveEntitlement {
  id: string;
  totalDays: number;
  carryForwardDays: number;
  isActive: boolean;
}

export enum LeaveRequestStatus {
  Pending = 0,
  Approved = 1,
  Rejected = 2,
  Cancelled = 3,
}

export const LeaveRequestStatusLabels: Record<number, string> = {
  0: 'Pending',
  1: 'Approved',
  2: 'Rejected',
  3: 'Cancelled',
};

export const LeaveRequestStatusColors: Record<number, 'warning' | 'success' | 'error' | 'default'> = {
  0: 'warning',
  1: 'success',
  2: 'error',
  3: 'default',
};

export interface LeaveRequestDto {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  departmentName: string | null;
  leaveTypeId: string;
  leaveTypeName: string;
  leaveTypeColor: string | null;
  leavePeriodId: string;
  leavePeriodName: string;
  startDate: string;
  endDate: string;
  numberOfDays: number;
  isHalfDay: boolean;
  halfDayType: string | null;
  reason: string;
  attachmentPath: string | null;
  attachmentFileName: string | null;
  hasAttachment: boolean;
  status: number;
  statusText: string;
  adminRemarks: string | null;
  approvedById: string | null;
  approvedByName: string | null;
  approvedAt: string | null;
  createdAt: string;
}

export interface CreateLeaveRequest {
  employeeId?: string;
  leaveTypeId: string;
  leavePeriodId: string;
  startDate: string;
  endDate: string;
  numberOfDays: number;
  isHalfDay: boolean;
  halfDayType?: string;
  reason: string;
  attachment?: File | null;
}

export interface LeaveBalanceDto {
  leaveTypeId: string;
  leaveTypeName: string;
  leaveTypeColor: string | null;
  totalDays: number;
  usedDays: number;
  carryForwardDays: number;
  remainingDays: number;
  leavePeriodId: string;
  leavePeriodName: string;
}

export interface WhoIsOnLeaveDto {
  leaveRequestId: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  departmentName: string | null;
  leaveTypeId: string;
  leaveTypeName: string;
  leaveTypeColor: string | null;
  startDate: string;
  endDate: string;
  numberOfDays: number;
  isHalfDay: boolean;
}

export interface AssignLeave {
  employeeId: string;
  leaveTypeId: string;
  leavePeriodId: string;
  startDate: string;
  endDate: string;
  numberOfDays: number;
  isHalfDay: boolean;
  halfDayType?: string;
  reason: string;
}

export interface HolidayDto {
  id: string;
  name: string;
  date: string;
  description: string | null;
  applicableFor: string;
  departmentId: string | null;
  departmentName: string | null;
  targets?: HolidayTargetDto[];
  year: number;
  isActive: boolean;
}

export interface HolidayTargetDto {
  id?: string;
  scopeType: 'Organization' | 'Branch' | 'Department' | string;
  scopeId: string;
}

export interface CreateHoliday {
  name: string;
  date: string;
  description?: string;
  applicableFor: string;
  departmentId?: string;
  targets?: HolidayTargetDto[];
  year: number;
}

export interface UpdateHoliday extends CreateHoliday {
  id: string;
  isActive: boolean;
}

export interface LeaveUsageReportDto {
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  departmentName: string | null;
  leaveUsage: LeaveUsageItemDto[];
  totalEntitled: number;
  totalCarryForward: number;
  totalUsed: number;
  totalRemaining: number;
}

export interface LeaveUsageItemDto {
  leaveTypeId: string;
  leaveTypeName: string;
  colorCode: string | null;
  entitled: number;
  used: number;
  carryForward: number;
  remaining: number;
}

// ═══════════ Billing & Subscription Types ═══════════

export interface SubscriptionDto {
  id: string;
  planName: string;
  description: string;
  status: string;
  billingCycle: string;
  activeEmployees: number;
  purchasedLicenses: number;
  nextBillingOn: string;
  currency: string;
  pricePerEmployee: number;
  totalAmount: number;
  hasActiveCancellationRequest?: boolean;
  cancellationRequestedAt?: string | null;
}

export interface PaymentDueDto {
  id: string;
  invoiceId: string;
  invoiceDate: string;
  dueDate: string;
  amountDue: number;
  status: string;
  currency: string;
}

export interface OrderInvoiceDto {
  id: string;
  invoiceReceiptId: string;
  invoiceDate: string;
  paymentDate: string;
  transactionAmount: number;
  currency: string;
  downloadUrl: string | null;
}

export interface CancellationRequest {
  reason: string;
  comments?: string;
}

// Geo Location Tracking
export interface LocationLogDto {
  id: string;
  employeeId: string;
  employeeName: string;
  latitude: number;
  longitude: number;
  locationName: string | null;
  source: string | null;
  capturedAt: string;
}

export interface LocationReportDto {
  employeeId: string;
  employeeName: string;
  locationName: string;
  latitude: number;
  longitude: number;
  arrivalTime: string;
  departureTime: string | null;
  durationMinutes: number;
  distanceKm: number;
  date: string;
  source: string;
  mode: string;
}

export interface SaveLocationRequest {
  employeeId: string;
  latitude: number;
  longitude: number;
  locationName?: string;
  source?: string;
}
